/**
 * Cross browser compatible intersection observer.
 *
 * Run a callback on an element when that element is in view, and an optional
 * second callback when that element is out of view (after having been in view).
 *
 * If intersectionObserver isn't supported, fallback to scroll event (debounced by
 * default).
 *
 * Callback functions take a single argument, element, which is a single DOM element.
 * Return true if the element should only be processed once, false if it should
 * continue to be watched.
 *
 * Should only be initialised after document load event.
 */

import { offset } from './helpers';

// Returns an array of objects representing each element, adding ability to cache if the element is visible
// or not.
function fallbackElementsParser(elements) {
    return elements.map(element => ({ element, visible: false }));
}

// Pretty self explanatory. Sets up the elements and then returns the required callback function.
function setupFallback(els, callbackIn, callbackOut, debounce) {
    let fallback = null;
    let active = false; // For controlling fallback debouncing

    // Set up the elements with extra data.
    let elements = fallbackElementsParser(els);

    // The actual processing to be done by the fallback.
    function fallbackAction() {
        // Cache scroll position (compatible)
        const scrollY = window.pageYOffset;
        const bottom = scrollY + window.innerHeight;

        elements.forEach(el => {
            const rect = offset(el.element);
            let remove = false;

            if (bottom > rect.top && scrollY < rect.bottom && getComputedStyle(el.element).display !== 'none') {
                // We can see it. Run callback and test for removal.
                if (!el.visible) {
                    remove = callbackIn(el.element);
                    el.visible = true;
                }
            } else if (el.visible && callbackOut) {
                // We can't see it. Run callbackOff and test for removal.
                el.visible = false;
                remove = callbackOut(el.element);
            }

            // Stop watching the element if required.
            if (remove) elements = elements.filter(elx => elx.element !== el.element);

            if (elements.length === 0) {
                // All elements processed/removed, stop watching.
                document.removeEventListener('scroll', fallback);
                window.removeEventListener('resize', fallback);
                window.removeEventListener('orientationchange', fallback);
            }
        });
    }

    // Debouncing fallback wrapper
    function intersectionFallback() {
        if (!active) {
            active = true;
            fallbackAction();
            setTimeout(() => (active = false), debounce);
        }
    }

    // Return either the debounced function if debounce has value or just the action function.
    fallback = debounce > 0 ? intersectionFallback : fallbackAction;
    document.addEventListener('scroll', fallback);
    window.addEventListener('resize', fallback);
    window.addEventListener('orientationchange', fallback);

    // Run once to handle if page is loaded already scrolled.
    fallback();
}

/**
 * All callbacks should return true to only be run once per element, false to be persistent.
 *
 * @param {Array}       elements            Array of DOM elements to process.
 * @param {function}    callbackIn          Callback to be run when element is in view.
 * @param {function}    callbackOut         (Optional) Callback to be run when element leaves view.
 * @param {Number}      debounce            (Optional) Value to debounce fallback by. Set to 0 to disable. Default 200ms.
 * @param {Boolean}     forceOnScroll       (Optional) If true, force use of scroll instead of intersectionObserver.
 * @returns {Boolean}
 */
function isInView(elements, callbackIn, callbackOut = null, debounce = 200, forceOnScroll = false) {
    // Use intersection observer if available.
    if (
        !forceOnScroll &&
        'IntersectionObserver' in window &&
        'IntersectionObserverEntry' in window &&
        'isIntersecting' in window.IntersectionObserverEntry.prototype
    ) {
        const elementObserver = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                let remove = false;
                if (entry.isIntersecting) remove = callbackIn(entry.target);
                else if (callbackOut) remove = callbackOut(entry.target);
                if (remove) elementObserver.unobserve(entry.target);
            });
        });
        elements.forEach(el => elementObserver.observe(el));
        return true;
    }

    // Use the fallback method.
    setupFallback(elements, callbackIn, callbackOut, debounce);
    return false;
}

export default isInView;
